import React, { useState, useEffect } from 'react';
import { CheckCircle, Lock, Shield, Unlock } from 'lucide-react';
import LogoString from '../../assets/images/logo_string.svg';
import { useSendMoney } from '../../contexts/SendMoneyContext';
import { EscrowService } from '../../services/escrowService';
import toast from 'react-hot-toast';

interface TransactionProcessingModalProps {
  // isOpen: boolean;
  amount: number;
  senderCompany: string;
  receiverCompany: string;
  transactionId: string;
  onComplete: (transactionData: any) => void;
  duration: number;
  receiverCompanyWallet?: string;
  wallet?: any;
}

interface ProcessingStage {
  id: string;
  name: string;
  icon: React.ReactNode;
  completed: boolean;
  current: boolean;
}

const TransactionProcessingModal: React.FC<TransactionProcessingModalProps> = ({
  // isOpen,
  amount,
  senderCompany,
  receiverCompany,
  transactionId,
  onComplete,
  duration,
  receiverCompanyWallet,
  wallet,
}) => {
  const [progress, setProgress] = useState(0);
  const [currentStage, setCurrentStage] = useState(0);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [escrowResult, setEscrowResult] = useState<any>(null);
  const [isEscrowFlow, setIsEscrowFlow] = useState(false);
  const [elapsedTimeInterval, setElapsedTimeInterval] = useState<NodeJS.Timeout | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);

  const {showProcessing, setEscrowStatus, escrowMessage, closeModal} = useSendMoney();

  const stages: ProcessingStage[] = [
    {
      id: 'locking',
      name: 'Locking funds',
      icon: <Lock className="h-4 w-4" />,
      completed: currentStage > 0,
      current: currentStage === 0
    },
    {
      id: 'verifying',
      name: 'Verifying',
      icon: <Shield className="h-4 w-4" />,
      completed: currentStage > 1,
      current: currentStage === 1
    },
    {
      id: 'releasing',
      name: 'Releasing',
      icon: <Unlock className="h-4 w-4" />,
      completed: currentStage > 2,
      current: currentStage === 2
    }
  ];

  const escrowStages: ProcessingStage[] = [
    {
      id: 'creating_hold',
      name: 'Creating hold',
      icon: <Lock className="h-4 w-4" />,
      completed: currentStage > 0,
      current: currentStage === 0
    },
    {
      id: 'validating',
      name: 'Validating',
      icon: <Shield className="h-4 w-4" />,
      completed: currentStage > 1,
      current: currentStage === 1
    },
    {
      id: 'finalizing',
      name: 'Finalizing',
      icon: <CheckCircle className="h-4 w-4" />,
      completed: currentStage > 2,
      current: currentStage === 2
    }
  ];

  useEffect(() => {
    // console.log('showProcessing', showProcessing);
    setIsOpen(showProcessing);
  }, [showProcessing]);

  useEffect(() => {
    if (!isCompleted) return;
    onComplete({
      transactionId: escrowResult.holdId,
      duration: (elapsedTime / 1000).toFixed(1),
      amount,
      senderCompany,
      receiverCompany,
      slot: escrowResult.slot,
      protocolFee: amount * 0.0008,
      legacyCost: amount * 0.025,
      savings: (amount * 0.025) - (amount * 0.0008),
      escrowResult: escrowResult,
      isEscrowFlow: true
    });
  }, [elapsedTime, isCompleted]);

  useEffect(() => {
    // console.log('isOpen', showProcessing);
    if (!isOpen) return;

    // Check if this is an escrow flow
    const shouldUseEscrowFlow = receiverCompanyWallet && wallet;
    setIsEscrowFlow(shouldUseEscrowFlow);

    if (shouldUseEscrowFlow) {
      // Start escrow flow
      startEscrowFlow().then((result) => {
        if (!result) return;
        console.log('elapsedTime', elapsedTime);
        setTimeout(() => {
          setIsCompleted(true);
        }, 500);
      });
    } else {
      // Use legacy flow
      startLegacyFlow();
    }
  }, [isOpen]);

  useEffect(() => {
    if (elapsedTimeInterval) clearInterval(elapsedTimeInterval);
    if (!startTime) return;
    const interval = setInterval(() => {
      setElapsedTime(Date.now() - startTime);
    }, 100);
    setElapsedTimeInterval(interval);
  }, [startTime]);

  const startEscrowFlow = async () => {
    try {
      const result = await EscrowService.processEscrowFlow(
        receiverCompanyWallet!,
        amount,
        wallet!,
        (status, message) => {
          setEscrowStatus(status, message);
          
          // Update progress based on status
          switch (status) {
            case 'CREATING_HOLD':
              setCurrentStage(0);
              setProgress(20);
              break;
            case 'HOLD_CREATED':
              setStartTime(Date.now());
              setProgress(40);
              break;
            case 'VALIDATING':
              setCurrentStage(1);
              setProgress(60);
              break;
            case 'VALIDATION_PASSED':
            case 'VALIDATION_FAILED':
              setProgress(80);
              break;
            case 'COMMITTING':
            case 'VOIDING':
              setCurrentStage(2);
              setProgress(90);
              break;
            case 'COMPLETED':
              setProgress(100);
              break;
          }
        }
      );

      setEscrowResult(result);
      
      setStartTime(null);
      // Complete the transaction
      if (result.finalStatus === 'cancelled') {
        setEscrowStatus('ERROR', 'Escrow flow cancelled');
        closeModal(); // close the modal
        toast.error('Escrow flow cancelled');
        return;
      }
      
      // setTimeout(() => {
      //   onComplete({
      //     transactionId: result.holdId,
      //     duration: elapsedTime / 1000,
      //     amount,
      //     senderCompany,
      //     receiverCompany,
      //     slot: result.slot,
      //     protocolFee: amount * 0.0008,
      //     legacyCost: amount * 0.025,
      //     savings: (amount * 0.025) - (amount * 0.0008),
      //     escrowResult: result,
      //     isEscrowFlow: true
      //   });
      // }, 500);
      return result;
      
    } catch (error) {
      console.error('Escrow flow error:', error);
      setEscrowStatus('ERROR', 'Escrow flow failed');
    }
  };

  const startLegacyFlow = () => {
    const interval = setInterval(() => {
      if (!startTime) return;
      setElapsedTime(Date.now() - startTime);
    }, 100);

    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          clearInterval(interval);
          // Complete the transaction
          setTimeout(() => {
            onComplete({
              transactionId,
              duration: (duration / 1000).toFixed(1),
              amount,
              senderCompany,
              receiverCompany,
              protocolFee: amount * 0.0008,
              legacyCost: amount * 0.025,
              savings: (amount * 0.025) - (amount * 0.0008)
            });
          }, 500);
          return 100;
        }
        return prev + 1;
      });
    }, 30);

    const stageInterval = setInterval(() => {
      setCurrentStage(prev => {
        if (prev >= 2) {
          clearInterval(stageInterval);
          return 2;
        }
        return prev + 1;
      });
    }, 1000);

    return () => {
      clearInterval(interval);
      clearInterval(progressInterval);
      clearInterval(stageInterval);
    };
  };

  if (!isOpen) return null;

  const getCurrentStatusMessage = () => {
    if (isEscrowFlow && escrowMessage) {
      return escrowMessage;
    }
    
    if (isEscrowFlow) {
      switch (currentStage) {
        case 0:
          return 'Creating escrow hold...';
        case 1:
          return 'Performing validation checks...';
        case 2:
          return 'Finalizing transaction...';
        default:
          return 'Processing escrow settlement...';
      }
    } else {
      switch (currentStage) {
        case 0:
          return 'Locking funds in escrow...';
        case 1:
          return 'Verifying with smart contracts...';
        case 2:
          return 'Releasing funds...';
        default:
          return 'Processing settlement...';
      }
    }
  };

  const getCurrentStages = () => {
    return isEscrowFlow ? escrowStages : stages;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative flex flex-col justify-center items-center mx-auto px-[10%] pt-12 w-full max-w-[80%] h-auto shadow-lg rounded-md bg-[#0E131A]">
        <div className="absolute top-12 left-12">
          <img src={LogoString} alt="Logo" className="h-[26px] w-auto" />
        </div>
        
        <div className="flex flex-col justify-center items-center mb-8">
          <h3 className="text-[44px] font-medium text-white mb-4">Loop Settlement Platform</h3>
          <p className="text-[#567095] text-[24px]">Enterprise settlement demo - instant, atomic, auditable</p>
        </div>

        {/* Central Icon */}
        <div className="relative mb-8">
          <div className="w-32 h-32 bg-gray-600 rounded-lg flex items-center justify-center opacity-50">
            <Lock className="h-16 w-16 text-gray-400" />
          </div>
          {/* Floating particles */}
          <div className="absolute -top-2 -right-2 w-3 h-3 bg-blue-400 rounded-full animate-pulse"></div>
          <div className="absolute top-4 -left-4 w-2 h-2 bg-blue-300 rounded-full animate-pulse delay-300"></div>
          <div className="absolute -bottom-2 left-4 w-2 h-2 bg-blue-500 rounded-full animate-pulse delay-700"></div>
        </div>

        {/* Transaction Flow */}
        <div className="flex items-center justify-center space-x-4 mb-8">
          <div className="flex items-center space-x-2">
            <div className="w-12 h-12 bg-blue-500 rounded flex items-center justify-center">
              <span className="text-white text-lg font-bold">W</span>
            </div>
            <span className="text-white text-sm">{senderCompany}</span>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
          </div>
          
          <div className="bg-gray-700 px-4 py-2 rounded">
            <span className="text-white font-bold">${amount.toLocaleString()}</span>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className="w-12 h-12 bg-red-500 rounded flex items-center justify-center">
              <span className="text-white text-lg font-bold">T</span>
            </div>
            <span className="text-white text-sm">{receiverCompany}</span>
          </div>
        </div>

        {/* Transaction Info */}
        <div className="flex items-center space-x-8 mb-8 text-sm">
          <div>
            <span className="text-gray-400">Transaction: </span>
            <span className="text-white">{transactionId.slice(0, 8)}...{transactionId.slice(-8)}</span>
          </div>
          <div>
            <span className="text-gray-400">Time elapsed: </span>
            <span className="text-white">{(elapsedTime / 1000).toFixed(1)}s</span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full max-w-md mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-gray-400 text-sm">Process settlement...</span>
            <span className="text-white text-sm">%{Math.round(progress)}</span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2">
            <div 
              className="bg-blue-500 h-2 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Process Steps */}
         <div className="flex items-center justify-center space-x-8 mb-8">
           {getCurrentStages().map((stage) => (
            <div key={stage.id} className="flex items-center space-x-2">
              {stage.completed ? (
                <CheckCircle className="h-5 w-5 text-blue-500" />
              ) : (
                <div className="h-5 w-5 rounded-full border-2 border-gray-600 flex items-center justify-center">
                  {stage.current && (
                    <div className="h-2 w-2 bg-blue-500 rounded-full animate-pulse"></div>
                  )}
                </div>
              )}
              <span className={`text-sm ${stage.completed ? 'text-white' : stage.current ? 'text-blue-400' : 'text-gray-500'}`}>
                {stage.name}
              </span>
            </div>
          ))}
        </div>

        {/* Current Status */}
        <div className="text-center">
          <p className="text-white text-lg">{getCurrentStatusMessage()}</p>
        </div>

        <div className="mt-8 text-center text-gray-400 text-sm">
          © 2025 LoopChain
        </div>
      </div>
    </div>
  );
};

export default TransactionProcessingModal;
